import React from 'react';
import {
  Box, Paper, Typography, Button, Chip, TextField, Alert, Grid,
  Accordion, AccordionSummary, AccordionDetails,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import IncidentDrawerTimelineTab from './IncidentDrawerTimelineTab';
import { formatFollowUpStateLabel } from '../../utils/incidentStateLabels';
import type { IncidentSeverity } from '../../services/incidentsApi';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';

function formatCloseReasonLabel(value: unknown): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not recorded';
  return raw
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

export function IncidentDrawerDetailStep8Closeout({ scope }: { scope: IncidentDrawerDetailScope }) {
  const s = scope as Record<string, any>;
  const IncidentEvidenceAttachmentPreview = s.IncidentEvidenceAttachmentPreview as React.ComponentType<{
    incidentId: string;
    filename: string;
    variant: 'image' | 'video';
    fetchEvidenceFileBlob: (incidentId: string, filename: string) => Promise<Blob>;
  }>;
  const workflowStatus = String(s.selectedIncident?.status || '').trim().toUpperCase();
  const workflowStateLabel = workflowStatus === 'SUPPRESSED'
    ? 'Suppressed'
    : workflowStatus === 'CLOSED'
      ? 'Closed'
      : workflowStatus
        ? workflowStatus.replace(/\b\w/g, (match) => match.toUpperCase())
        : 'Open';
  const showFinalWorkflowState = workflowStatus === 'CLOSED' || workflowStatus === 'SUPPRESSED';
  const closeReasonLabel = formatCloseReasonLabel(s.selectedIncident?.close_reason);
  const closeNote = String(s.selectedIncident?.close_note || '').trim();
  return (
    <Box ref={s.drawerStep8Ref} sx={s.stepSectionSx('step8')}>
      <Paper variant="outlined" sx={s.drawerCardSx}>
        <Typography sx={s.cardLabelSx}>{s.journeyPhaseTitleForStep('step8')} phase</Typography>
        <Typography sx={s.cardHeadingSx}>Review notes</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.35, mt: 0.45, lineHeight: 1.55 }}>
          Capture operator context and handoff notes while you review, close, or route this incident.
        </Typography>
        <TextField
          fullWidth
          minRows={3}
          multiline
          inputRef={s.commentInputRef}
          value={s.noteDraft}
          disabled={!s.canCommentOnIncident}
          onChange={(e) => s.setNoteDraft(e.target.value)}
          placeholder="Add a comment for the audit trail…"
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {s.incidentCommentsLoading ? 'Loading comments…' : `Saved comments: ${s.incidentComments.length || 0}`}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={!s.selectedIncident?.incident_id || !!s.drawerBusy || !s.canCommentOnIncident}
            onClick={s.handleSaveNote}
          >
            Save comment
          </Button>
        </Box>
        {s.incidentCommentsError && <Alert severity="warning" sx={{ mt: 1 }}>{s.incidentCommentsError}</Alert>}
      </Paper>

      <Paper variant="outlined" sx={s.drawerCardSx}>
        <Typography sx={s.cardLabelSx}>{s.journeyPhaseTitleForStep('step8')} phase</Typography>
        {s.renderAuditStepHeading(
          'step8',
          'Step 8: Confirm the review record',
          s.groupedReviewMode
            ? undefined
            : 'Make sure the review record is understandable before you finish: confirm the history is readable, comments are captured, and the supporting evidence is attached.',
        )}
        <Alert severity={s.closeoutGuidance.severity} sx={{ mb: 1.35 }}>
          {s.closeoutGuidance.message}
        </Alert>
        {showFinalWorkflowState ? (
          <Alert severity="success" sx={{ mb: 1.35 }} data-testid="closeout-final-state">
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {workflowStateLabel === 'Suppressed' ? 'Representative incident suppressed' : 'Representative incident closed'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.4, lineHeight: 1.55 }}>
              Workflow state: {workflowStateLabel}. Close reason: {closeReasonLabel}.
            </Typography>
            {closeNote ? (
              <Typography variant="body2" sx={{ mt: 0.6, lineHeight: 1.55 }}>
                {closeNote}
              </Typography>
            ) : null}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.9, mb: 1.35 }}>
          <Chip size="small" color={showFinalWorkflowState ? 'success' : 'default'} label={`Workflow state: ${workflowStateLabel}`} />
          <Chip size="small" color={s.closeoutHistoryState.color} label={s.closeoutHistoryState.label} />
          <Chip size="small" color={s.closeoutCommentState.color} label={s.closeoutCommentState.label} />
          <Chip size="small" color={s.closeoutEvidenceState.color} label={s.closeoutEvidenceState.label} />
          <Chip size="small" variant="outlined" label={`Follow-up state: ${formatFollowUpStateLabel(s.opsStateDraft || s.selectedIncident?.ops_state || '')}`} />
        </Box>
        <IncidentDrawerTimelineTab incidentActivityError={s.incidentActivityError} incidentActivityLoading={s.incidentActivityLoading} incidentActivityItems={s.incidentActivityItems} incidentComments={s.incidentComments} incidentCommentsLoading={s.incidentCommentsLoading} incidentCommentsError={s.incidentCommentsError} renderMode={s.renderMode} siteTimeZone={s.siteTimeZone}
        />
        <Accordion
          disableGutters
          sx={{
            mt: 1.35,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 'none',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Supporting detail
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Severity adjustments and attached files stay here for reference without crowding the closeout path.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Box>
                <Typography sx={{ ...s.cardHeadingSx, fontSize: 15 }}>Severity override</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.1, mt: 0.35, lineHeight: 1.55 }}>
                  Adjust only when the evidence supports a different risk level than the original alert.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Severity</InputLabel>
                    <Select
                      label="Severity"
                      value={s.severityDraft}
                      disabled={!s.canOverrideIncidentSeverity}
                      onChange={(e) => s.setSeverityDraft(e.target.value as IncidentSeverity)}
                    >
                      <MenuItem value="critical">critical</MenuItem>
                      <MenuItem value="high">high</MenuItem>
                      <MenuItem value="medium">medium</MenuItem>
                      <MenuItem value="low">low</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!s.selectedIncident?.incident_id || !!s.drawerBusy || !s.canOverrideIncidentSeverity}
                    onClick={() => void s.handleOverrideSeverity(s.severityDraft)}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
              {s.attachmentCount > 0 ? (
                <Box>
                  <Typography sx={{ ...s.cardHeadingSx, fontSize: 15, mb: 1 }}>Attachments</Typography>
                  <Grid container spacing={2}>
                    {(s.selectedIncident.evidence?.attachments || [])
                      .slice()
                      .sort((a: any, b: any) => {
                        const ta =
                          s.parseTimingMs(a?.metadata?.evidence_ts_ms) ||
                          s.parseTimingMs(a?.metadata?.captured_at) ||
                          s.parseTimingMs(a?.metadata?.timestamp) ||
                          s.parseEvidenceFilenameMs(a?.filename) ||
                          0;
                        const tb =
                          s.parseTimingMs(b?.metadata?.evidence_ts_ms) ||
                          s.parseTimingMs(b?.metadata?.captured_at) ||
                          s.parseTimingMs(b?.metadata?.timestamp) ||
                          s.parseEvidenceFilenameMs(b?.filename) ||
                          0;
                        return ta - tb;
                      })
                      .map((att: any) => {
                        const isVideo = String(att.type || '').toLowerCase() === 'video' || /\.(mkv|mp4|webm)$/i.test(String(att.filename || ''));
                        const isImage = String(att.type || '').toLowerCase() === 'snapshot' || /\.(jpg|jpeg|png|gif)$/i.test(String(att.filename || ''));
                        const edgeEvidenceAtMs =
                          s.parseTimingMs(att?.metadata?.evidence_ts_ms) ||
                          s.parseTimingMs(att?.metadata?.captured_at) ||
                          s.parseTimingMs(att?.metadata?.timestamp) ||
                          s.parseEvidenceFilenameMs(att?.filename);
                        return (
                          <Grid item xs={12} md={6} key={att.filename}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                    {att.filename}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {att.type || 'attachment'}
                                    {edgeEvidenceAtMs ? ` • edge ${new Date(edgeEvidenceAtMs).toLocaleString()}` : att.uploaded_at ? ` • cloud ${new Date(att.uploaded_at).toLocaleString()}` : ''}
                                  </Typography>
                                </Box>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<OpenInNewIcon />}
                                  onClick={async () => {
                                    try {
                                      const blob = await s.fetchEvidenceFileBlob(s.selectedIncident.incident_id, att.filename);
                                      s.downloadBlob(blob, att.filename || 'attachment');
                                    } catch {
                                      // ignore
                                    }
                                  }}
                                >
                                  Open
                                </Button>
                              </Box>
                              {isImage ? (
                                <IncidentEvidenceAttachmentPreview
                                  incidentId={s.selectedIncident.incident_id}
                                  filename={att.filename}
                                  variant="image"
                                  fetchEvidenceFileBlob={s.fetchEvidenceFileBlob}
                                />
                              ) : isVideo ? (
                                <IncidentEvidenceAttachmentPreview
                                  incidentId={s.selectedIncident.incident_id}
                                  filename={att.filename}
                                  variant="video"
                                  fetchEvidenceFileBlob={s.fetchEvidenceFileBlob}
                                />
                              ) : (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                  Preview not available for this attachment type.
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                        );
                      })}
                  </Grid>
                </Box>
              ) : null}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>
      {!s.groupedReviewMode ? s.renderStepNavigation('step8') : null}
    </Box>
  );
}
